/**
 * useEvidencePanelMode · Wave 392 · 2026-05-14
 *
 * Returns 'panel' when the viewport is >= 1280px (Tailwind xl breakpoint),
 * 'modal' otherwise. Re-emits on resize via matchMedia change events.
 *
 * SSR-safe: returns 'modal' when window is undefined (no matchMedia API).
 *
 * Used by EvidenceViewerProvider to set the context's viewMode, which drives
 * the split-panel layout on TrenaEstimatePage and disables the modal on wide
 * viewports.
 */

import { useEffect, useState } from 'react';

export type EvidenceViewMode = 'panel' | 'modal';

const BREAKPOINT_QUERY = '(min-width: 1280px)';

function readViewMode(): EvidenceViewMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'modal';
  }
  return window.matchMedia(BREAKPOINT_QUERY).matches ? 'panel' : 'modal';
}

export function useEvidencePanelMode(): EvidenceViewMode {
  const [mode, setMode] = useState<EvidenceViewMode>(readViewMode);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(BREAKPOINT_QUERY);

    function handleChange(e: MediaQueryListEvent) {
      setMode(e.matches ? 'panel' : 'modal');
    }

    // Modern browsers — addEventListener is the stable API.
    mq.addEventListener('change', handleChange);
    // Re-read on mount in case the viewport changed between SSR render and hydration.
    setMode(mq.matches ? 'panel' : 'modal');

    return () => {
      mq.removeEventListener('change', handleChange);
    };
  }, []);

  return mode;
}
